import React from "react";
import { format } from "date-fns";
import { XMarkIcon } from "@heroicons/react/24/outline";

const DepositDetailsModal = ({ deposit, onClose, onInvoiceClick }) => {
  if (!deposit) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const formatDate = (date) => {
    try {
      const parsedDate = new Date(date);
      // Use UTC methods to get the intended date components
      const year = parsedDate.getUTCFullYear();
      const month = parsedDate.getUTCMonth();
      const day = parsedDate.getUTCDate();
      // Create a new date in local timezone with the UTC date components
      const localDate = new Date(year, month, day);
      return format(localDate, "MMM dd, yyyy");
    } catch (error) {
      console.error('[Deposit Details Modal] Date formatting error:', error, 'for date:', date);
      return "Invalid Date";
    }
  };

  // Calculate total used from deposit usage (for invoices)
  const totalUsedForInvoices = (deposit.depositUsage || []).reduce((sum, usage) => sum + (usage.amountUsed || 0), 0);
  // Use depositBalance from backend if available, otherwise calculate
  const remainingBalance = deposit.depositBalance !== undefined ? deposit.depositBalance : (deposit.amount - totalUsedForInvoices);
  // Total amount used (invoices + refunds) = original amount - remaining balance
  const totalUsed = deposit.amount - remainingBalance;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Deposit Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {formatDate(deposit.paymentDate || deposit.createdAt)} • {deposit.paymentMethod?.charAt(0).toUpperCase() + deposit.paymentMethod?.slice(1)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Deposit Summary */}
          <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(deposit.amount)}
              </div>
              <div className="text-sm text-gray-600">Original Deposit</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(totalUsed)}
              </div>
              <div className="text-sm text-gray-600">Amount Used</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${remainingBalance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatCurrency(remainingBalance)}
              </div>
              <div className="text-sm text-gray-600">Remaining Balance</div>
            </div>
          </div>

        </div>

        {/* Refund Information */}
        {deposit.refund && (
          <div className="p-6 border-b border-gray-200 bg-red-50">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Refund Information
            </h3>
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-700">Refund Amount</div>
                  <div className="text-lg font-semibold text-red-600">{formatCurrency(deposit.refund.refundAmount)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Refund Date</div>
                  <div className="text-sm text-gray-900">{formatDate(deposit.refund.refundDate)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Refund Method</div>
                  <div className="text-sm text-gray-900">{deposit.refund.refundMethod?.charAt(0).toUpperCase() + deposit.refund.refundMethod?.slice(1)}</div>
                </div>
                {deposit.refund.refundedBy && (
                  <div>
                    <div className="text-sm font-medium text-gray-700">Refunded By</div>
                    <div className="text-sm text-gray-900">{deposit.refund.refundedBy.firstName} {deposit.refund.refundedBy.lastName}</div>
                  </div>
                )}
              </div>
              {deposit.refund.refundReason && (
                <div className="mt-3">
                  <div className="text-sm font-medium text-gray-700">Reason</div>
                  <div className="text-sm text-gray-900">{deposit.refund.refundReason}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoice Usage List */}
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Applied to Invoices {deposit.depositUsage?.length > 0 && `(${deposit.depositUsage.length})`}
          </h3>
          
          {!deposit.depositUsage || deposit.depositUsage.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {deposit.refund ? (
                <>
                  <div className="text-sm">This deposit has been refunded.</div>
                  <div className="text-xs mt-1 space-y-1">
                    <div>Refund Amount: {formatCurrency(deposit.refund.refundAmount)}</div>
                    <div>Refund Date: {formatDate(deposit.refund.refundDate)}</div>
                    <div>Refund Method: {deposit.refund.refundMethod?.charAt(0).toUpperCase() + deposit.refund.refundMethod?.slice(1)}</div>
                    {deposit.refund.refundedBy && (
                      <div>Refunded by: {deposit.refund.refundedBy.firstName} {deposit.refund.refundedBy.lastName}</div>
                    )}
                    {deposit.refund.refundReason && (
                      <div>Reason: {deposit.refund.refundReason}</div>
                    )}
                  </div>
                </>
              ) : remainingBalance === 0 && totalUsed > 0 ? (
                <>
                  <div className="text-sm">This deposit has been fully used.</div>
                  <div className="text-xs mt-1">
                    {totalUsedForInvoices > 0 ? 
                      `Applied to invoices: ${formatCurrency(totalUsedForInvoices)}${totalUsed > totalUsedForInvoices ? `, Refunded: ${formatCurrency(totalUsed - totalUsedForInvoices)}` : ''}` :
                      `Refunded: ${formatCurrency(totalUsed)}`
                    }
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm">This deposit has not been applied to any invoices yet.</div>
                  <div className="text-xs mt-1">Full amount remains available for future use.</div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {deposit.depositUsage.map((usage, index) => (
                <div
                  key={usage._id || index}
                  onClick={() => onInvoiceClick && onInvoiceClick(usage.invoice)}
                  className={`p-4 border border-gray-200 rounded-lg transition-all duration-200 ${
                    onInvoiceClick ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="font-medium text-gray-900">
                          {usage.invoice?.invoiceId || usage.invoiceNumber || 'Unknown Invoice'}
                        </div>
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          {usage.invoice?.status || 'Applied'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Applied on {formatDate(usage.dateUsed)}
                        {usage.invoice?.serviceDate && (
                          <span className="mx-2">•</span>
                        )}
                        {usage.invoice?.serviceDate && (
                          <span>Service: {formatDate(usage.invoice.serviceDate)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        -{formatCurrency(usage.amountUsed)}
                      </div>
                      {usage.invoice?.amount && (
                        <div className="text-xs text-gray-500">
                          of {formatCurrency(usage.invoice.amount)}
                        </div>
                      )}
                    </div>
                  </div>
                  {onInvoiceClick && (
                    <div className="mt-2 text-xs text-blue-600">
                      Click to view invoice details →
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositDetailsModal;